import 'theme';

class RtAngleVariantPicker extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.form = document.forms[this.getAttribute('form-id')];
    this.addEventListener('change', event => {
      if (!event.target.matches('input[data-option-position]') || !this.form) return;
      const selected = [...this.form.elements].filter(input => input.matches('input[data-option-position]:checked')).sort((a,b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      this.selectCombination(selected);
    }, {signal:this.controller.signal});
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.request?.abort();
  }
  async selectCombination(selected) {
    const owner = this.closest('rt-angle-product');
    if (!owner || owner.busy || owner.variantBusy) return;
    owner.setVariantBusy(true);
    this.inert = true;
    this.request = new AbortController();
    const timer = setTimeout(() => this.request.abort(), 15000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const productUrl = selected.find(input => input.dataset.productUrl)?.dataset.productUrl || root + 'products/' + this.getAttribute('handle');
      const url = new URL(productUrl, location.origin);
      url.searchParams.set('option_values', selected.map(input => input.value).join(','));
      url.searchParams.set('section_id', this.getAttribute('section-id'));
      const response = await fetch(url, {signal:this.request.signal});
      if (!response.ok) throw new Error('variant_response');
      const fragment = document.createRange().createContextualFragment(await response.text());
      const nextPicker = [...fragment.querySelectorAll('rt-angle-variant-picker')].find(picker => picker.getAttribute('form-id') === this.getAttribute('form-id'));
      if (!nextPicker) throw new Error('variant_markup');
      const previousVariant = JSON.parse(this.querySelector('script[data-variant]')?.textContent || 'null');
      const variant = JSON.parse(nextPicker.querySelector('script[data-variant]')?.textContent || 'null');
      const oldQuantity = this.form.elements.namedItem('quantity')?.value;
      const form = this.form;
      form.elements.namedItem('id').value = variant?.id || '';
      form.elements.namedItem('id').dispatchEvent(new Event('change', {bubbles:true}));
      form.dispatchEvent(new CustomEvent('product:rerender', {detail:{htmlFragment:fragment,productChange:false}}));
      form.dispatchEvent(new CustomEvent('variant:change', {bubbles:true,detail:{formId:form.id,variant,previousVariant}}));
      const quantity = form.elements.namedItem('quantity');
      if (quantity && oldQuantity && quantity.type !== 'hidden') {
        const minimum = Number(quantity.min) || 1;
        const step = Number(quantity.step) || 1;
        const maximum = Number(quantity.max) || Infinity;
        const validMaximum = Number.isFinite(maximum) ? minimum + Math.floor((maximum - minimum) / step) * step : maximum;
        quantity.value = Math.min(validMaximum, minimum + Math.ceil(Math.max(0, Number(oldQuantity) - minimum) / step) * step);
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
      owner.announce('');
      owner.dispatchEvent(new CustomEvent('rt-angle-variant-change', {bubbles:true,detail:{productId:owner.dataset.rtAngleProduct,variantId:variant?.id}}));
      window.Shopify?.PaymentButton?.init();
    } catch (error) {
      owner.announce(owner.dataset.error,true);
      owner.querySelectorAll('[data-rt-angle-submit]').forEach(button => {button.dataset.rtAngleUnavailable='true';button.disabled=true;});
    } finally {
      clearTimeout(timer);
      this.inert=false;
      owner.setVariantBusy(false);
    }
  }
}
if (!customElements.get('rt-angle-variant-picker')) customElements.define('rt-angle-variant-picker',RtAngleVariantPicker);

const rtAngleProducts = new Set();
let rtAngleFrame = 0;
function rtAngleStickyUpdate() {
  if (rtAngleFrame) return;
  rtAngleFrame = requestAnimationFrame(() => {
    rtAngleFrame = 0;
    const modal = document.querySelector('cart-drawer[open],x-modal[open],header-sidebar[open],x-drawer[open]');
    const editing = document.activeElement?.matches('input:not([type=radio]):not([type=checkbox]):not([type=submit]),textarea,select');
    const footer = document.querySelector('.shopify-section-group-footer-group');
    let active = null;
    for (const owner of rtAngleProducts) {
      const bar = owner.querySelector('[data-rt-angle-sticky]');
      const actions = owner.querySelector('[data-rt-angle-main-actions]');
      if (!bar || !actions) continue;
      const mobile = matchMedia('(max-width:999px)').matches;
      const enabled = bar.dataset[mobile ? 'mobile' : 'desktop'] === 'true';
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height')) || 0;
      const inlineVisible = bar.dataset.hideInline === 'true' && [...document.querySelectorAll('[data-rt-angle-product-submit]')].some(button => {
        if (button.dataset.rtAngleProductSubmit !== owner.dataset.rtAngleProduct) return false;
        const bounds = button.getBoundingClientRect();
        return bounds.height > 0 && bounds.top >= header && bounds.bottom <= innerHeight - 24;
      });
      if (enabled && !inlineVisible && actions.getBoundingClientRect().bottom < header) active = owner;
    }
    if (modal || editing || (footer && footer.getBoundingClientRect().top < innerHeight)) active = null;
    for (const owner of rtAngleProducts) {
      const bar = owner.querySelector('[data-rt-angle-sticky]');
      if (bar) bar.hidden = owner !== active;
    }
  });
}
class RtAngleProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.querySelectorAll('[data-rt-review-link]').forEach(link => {
      const href = link.getAttribute('href');
      if (href?.startsWith('#') && !document.getElementById(href.slice(1))) link.href = link.dataset.reviewFallback;
      link.addEventListener('click', () => {
        const target = document.getElementById(link.getAttribute('href')?.slice(1));
        if (link.getAttribute('href')?.startsWith('#') && target?.tagName === 'DETAILS') target.open = true;
      }, options);
    });
    this.form = this.querySelector('.rt-angle-form');
    if (!this.form) return;
    const variantInput = this.form.elements.namedItem('id');
    if (variantInput) variantInput.disabled = false;
    this.form.addEventListener('submit', event => this.submit(event), options);
    this.form.addEventListener('variant:change', () => {
      this.syncButtons();
      this.syncLinkedSummary();
      rtAngleStickyUpdate();
    }, options);
    this.addEventListener('click', event => {
      if (event.target.closest('[data-rt-angle-submit]') && (this.busy || this.variantBusy)) event.preventDefault();
    }, options);
    rtAngleProducts.add(this);
    this.syncLinkedSummary();
    window.addEventListener('scroll', rtAngleStickyUpdate, { passive: true, ...options });
    window.addEventListener('resize', rtAngleStickyUpdate, options);
    document.addEventListener('focusin', rtAngleStickyUpdate, options);
    document.addEventListener('focusout', rtAngleStickyUpdate, options);
    document.addEventListener('dialog:before-show', () => { const bar=this.querySelector('[data-rt-angle-sticky]'); if(bar)bar.hidden=true; }, options);
    document.addEventListener('dialog:after-hide', rtAngleStickyUpdate, options);
    rtAngleStickyUpdate();
  }
  disconnectedCallback() {
    this.controller?.abort();
    rtAngleProducts.delete(this);
  }
  announce(message, error = false) {
    const linkedFeedback = [...document.querySelectorAll('[data-rt-angle-product-summary]')].filter(summary => summary.dataset.rtAngleProductSummary === this.dataset.rtAngleProduct).flatMap(summary => [...summary.querySelectorAll('[data-rt-angle-linked-feedback]')]);
    [this.querySelector('[data-rt-angle-feedback]'), ...linkedFeedback].filter(Boolean).forEach(feedback => {
      feedback.setAttribute('role', error ? 'alert' : 'status');
      feedback.textContent = message;
    });
  }
  syncLinkedSummary() {
    const primary = this.form?.querySelector('[data-rt-angle-submit]');
    if (!primary) return;
    document.querySelectorAll('[data-rt-angle-product-summary]').forEach(summary => {
      if (summary.dataset.rtAngleProductSummary !== this.dataset.rtAngleProduct) return;
      const price = summary.querySelector('[data-rt-angle-linked-price]');
      const compare = summary.querySelector('[data-rt-angle-linked-compare]');
      const sourcePrice = this.querySelector('.rt-angle-purchase-price sale-price');
      const sourceCompare = this.querySelector('.rt-angle-purchase-price compare-at-price');
      const visibleMoney = source => {
        if (!source) return '';
        const copy = source.cloneNode(true);
        copy.querySelectorAll('.sr-only').forEach(label => label.remove());
        return copy.textContent.trim();
      };
      if (price && sourcePrice) price.textContent = visibleMoney(sourcePrice);
      if (compare) { compare.textContent = visibleMoney(sourceCompare); compare.hidden = !sourceCompare; }
      summary.querySelectorAll('[data-rt-angle-product-submit]').forEach(button => {
        button.textContent = primary.textContent;
        if (!this.busy && !this.variantBusy) button.disabled = primary.disabled;
      });
    });
  }
  syncButtons() {
    this.querySelectorAll('[data-rt-angle-submit]').forEach(button => {
      if (!this.busy && !this.variantBusy && button.dataset.rtAngleUnavailable === 'false') button.disabled = false;
    });
  }
  setVariantBusy(value) {
    this.variantBusy = value;
    this.setBusyButtons(value);
    if (!value) this.syncLinkedSummary();
  }
  setBusyButtons(value) {
    const linkedButtons = [...document.querySelectorAll('[data-rt-angle-product-submit]')].filter(button => button.dataset.rtAngleProductSubmit === this.dataset.rtAngleProduct);
    [...this.querySelectorAll('[data-rt-angle-submit]'), ...linkedButtons].forEach(button => {
      if (value) {
        button.dataset.rtAngleUnavailable = String(button.disabled);
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
        if (button.dataset.rtAngleUnavailable === 'false') button.disabled = false;
        delete button.dataset.rtAngleUnavailable;
      }
    });
    const express = this.querySelector('[data-block-id="rt-angle-express"]');
    if (express) express.inert = value;
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy || this.variantBusy) return;
    const variantInput = this.form.elements.namedItem('id');
    if (!variantInput?.value || !this.form.reportValidity()) return;
    if (event.submitter?.disabled || this.form.querySelector('[data-rt-angle-submit]')?.disabled) return;
    this.busy = true;
    this.setBusyButtons(true);
    this.announce('');
    const root = window.Shopify?.routes?.root || '/';
    let added = false;
    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const data = new FormData(this.form);
      data.set('sections', [...new Set(sections)].slice(0,5).join(','));
      data.set('sections_url', root + 'variants/' + variantInput.value);
      const response = await fetch(root + 'cart/add.js', { method: 'POST', body: data, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const result = await response.json();
      if (!response.ok) {
        this.announce(typeof result.description === 'string' ? result.description : this.dataset.error, true);
        this.form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: result.description } }));
        return;
      }
      added = true;
      const cartResponse = await fetch(root + 'cart.js');
      if (!cartResponse.ok) throw new Error('cart_refresh');
      const cart = await cartResponse.json();
      cart.sections = result.sections;
      const items = result.items || [result];
      this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart, onSuccessDo: 'force_open_drawer' } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart } }));
      this.announce(this.dataset.added);
      this.dispatchEvent(new CustomEvent('rt-angle-added', { bubbles: true, detail: { productId: this.dataset.rtAngleProduct, variantId: variantInput.value, quantity: data.get('quantity'), sectionId: this.dataset.rtAngleSection } }));
    } catch (error) {
      this.announce(added ? this.dataset.added : this.dataset.error, !added);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      if (added) document.querySelector('cart-drawer')?.show?.();
    } finally {
      this.busy = false;
      this.setBusyButtons(false);
      this.syncLinkedSummary();
    }
  }
}
if (!customElements.get('rt-angle-product')) customElements.define('rt-angle-product', RtAngleProduct);

document.addEventListener('click', event => {
  const button = event.target.closest('[data-rt-angle-product-submit]');
  if (!button || button.disabled) return;
  const owner = [...rtAngleProducts].find(product => product.dataset.rtAngleProduct === button.dataset.rtAngleProductSubmit);
  if (!owner?.form || owner.busy || owner.variantBusy) return;
  const primary = owner.form.querySelector('[data-rt-angle-submit]');
  if (primary && !primary.disabled) owner.form.requestSubmit(primary);
});
